<template>
  <div v-show="!isEmpty(checkItemHistList)">
    <el-row type="flex" class="title-bar" justify="space-between">
      <div>
        <span class="tit content-title">컨피그 변경</span>
        <span class="condition-text">({{ configPeriodLabel }})</span>
        <span v-if="isTrue(configData.isAbnormal)" class="abnormal-message">
          {{ configData.abnormalMessage }}
        </span>
      </div>
      <div>
        <ul class="diff-legend-box">
          <li><span class="item added"></span>추가</li>
          <li><span class="item modified"></span>변경</li>
          <li><span class="item deleted"></span>삭제</li>
        </ul>
        <el-radio-group class="ordinary cmp-margin-left" v-model="diffFlag">
          <el-radio-button :label="true">변경내역만 보기</el-radio-button>
          <el-radio-button :label="false">전체 보기</el-radio-button>
        </el-radio-group>
      </div>
    </el-row>
    <el-row class="title-bar config-subtitle-bar" type="flex">
      <div class="vertical-center-box" style="width:calc(50% - 15px);">
        <span class="mr-r10" style="font-weight:500">기준 컨피그</span>
        <el-select style="width:144px;" v-model="baseHistSeq" placeholder="변경 이력 선택" @change="compareHist">
          <el-option v-for="changeInfo in checkItemHistList" :key="changeInfo.seq" :label="getDateFormatter(changeInfo.checkDatetime)" :value="changeInfo.seq"></el-option>
        </el-select>
        <em v-if="isEqualHist" class="mr-l10">동일한 이력입니다.</em>
      </div>
      <div class="mr-l15" style="width:50%;">
        <el-row type="flex" justify="space-between">
          <div class="vertical-center-box">
            <span class="mr-r10" style="font-weight:500">비교대상 컨피그</span>
            <el-select style="width:144px;" v-model="compareHistSeq" placeholder="변경 이력 선택" @change="compareHist">
              <el-option v-for="changeInfo in allCheckItemHistList" :key="changeInfo.seq" :label="getDateFormatter(changeInfo.checkDatetime)" :value="changeInfo.seq"></el-option>
            </el-select>
          </div>
        </el-row>
      </div>
    </el-row>
    <el-row type="flex">
      <div style="width:100%;" v-html="showCompareText"></div>
    </el-row>
  </div>
</template>

<script>
import { isNil, isEmpty, concat } from 'lodash';
import { isTrue } from '@/utils/boolean';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import { faultCheckItemDefine } from '@/constants/netdevice';
import netdeviceInfoAPI from '@/api/config/netdevice/netdevice-info';
import { etcInfoDefine } from '../../../../constants/fault-analysis';
import { getConfigPeriodCondition, getPeriodConditionLabel } from '../../../../utils/fault-analysis';

export default {
  name: 'WConfigChangeHistView',
  props: {
    /**
     * 기타 분석 정보 조회 시 필요한 정보 객체 (동시성 문제 고려하여 하나의 객체로 전달)
     * 1) obj: 분석 대상 장비 정보
     * 2) eventHistData: 분석 이벤트 이력 정보
     * 3) etcInfo: 기타 분석 정보
     * @type {Object}
     */
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      baseHistSeq: null,
      compareHistSeq: null,
      configData: {},
      loadedConfigData: false,
      checkItemHistList: [],
      allCheckItemHistList: [],
      compareHistData: null,
      diffFlag: true,
      showCompareText: '',
      isEqualHist: false,
    };
  },
  computed: {
    configPeriodLabel() {
      if (isEmpty(this.data) || isEmpty(this.data.eventHistData)) {
        return EMPTY_TEXT;
      }
      const period = getConfigPeriodCondition(this.data.eventHistData.checkDatetime);
      return getPeriodConditionLabel(period.startDatetime, period.endDatetime);
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(to) {
        this.reset();
        if (!isNil(to)) {
          this.loadData();
        }
      },
    },
    diffFlag: {
      handler() {
        if (!isNil(this.compareHistData.compareData)) {
          this.setShowCompareText();
        }
      },
    },
  },
  methods: {
    isEmpty,
    isTrue,
    /**
     * [컨피그 변경] etcInfo에 전달된 컨피그 변경 데이터 바인딩
     * @author jeee12
     */
    loadData() {
      if (!isEmpty(this.data.etcInfo) && !isEmpty(this.data.etcInfo[etcInfoDefine.NETDEVICE_CONFIG_CHANGE_HIST])) {
        this.configData = this.data.etcInfo[etcInfoDefine.NETDEVICE_CONFIG_CHANGE_HIST];
        const checkItemHistList = this.configData.etcData.configData;
        this.checkItemHistList = checkItemHistList;
        if (isEmpty(this.configData.etcData.priorConfigData)) {
          this.allCheckItemHistList = checkItemHistList;
          this.baseHistSeq = checkItemHistList[0].seq; // Left: 기준 이력
          this.compareHistSeq = checkItemHistList[checkItemHistList.length > 1 ? 1 : 0].seq; // Right : 비교 이력
        } else {
          this.allCheckItemHistList = concat([this.configData.etcData.priorConfigData], checkItemHistList);
          this.compareHistSeq = this.allCheckItemHistList[0].seq; // Right : 비교 이력
          this.baseHistSeq = this.checkItemHistList[0].seq; // Left: 기준 이력
        }
        this.diffFlag = this.compareHistSeq !== this.baseHistSeq;
        this.compareHist();
      } else {
        this.clearCompareHistData();
      }
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      // 컨피그 변경
      this.clearCompareHistData();
    },
    /**
     * [컨피그 변경] 두 개의 변경 이력 비교
     * @author jeee12
     */
    compareHist() {
      const params = {
        objId: this.data.obj.objId,
        itemId: faultCheckItemDefine.CONFIG,
        requestKey: faultCheckItemDefine.CONFIG.toLowerCase(),
        baseHistSeq: this.baseHistSeq,
        compareHistSeq: this.compareHistSeq,
      };
      this.isEqualHist = this.baseHistSeq === this.compareHistSeq;
      netdeviceInfoAPI
        .getNetdeviceComparedData(params)
        .then((response) => {
          if (response.data.success) {
            const data = response.data.data;
            if (!isEmpty(data)) {
              this.setComparedData(data);
            } else {
              this.clearCompareHistData();
            }
          } else {
            this.clearCompareHistData();
          }
        })
        .catch(() => {
          this.clearCompareHistData();
        });
    },
    /**
     * [컨피그 변경] 변경 이력 비교 정보 설정
     * @author jeee12
     * @param  {[type]} data [description]
     */
    setComparedData(data) {
      this.compareHistData = data;
      if (this.loadedConfigData) {
        this.diffFlag = true;
      } else {
        this.loadedConfigData = true; // 최초 load할 때는 데이터 바인딩 하는 부분에서 diffFlag 설정
      }
      this.setShowCompareText();
    },
    /**
     * [컨피그 변경] 변경내역만 보기 / 전체 보기 설정
     * @author jeee12
     */
    setShowCompareText() {
      this.showCompareText = this.diffFlag ? this.compareHistData.compareData.diff : this.compareHistData.compareData.compare;
    },
    /**
     * [컨피그 변경] 비교 데이터 초기화
     * @author jeee12
     */
    clearCompareHistData() {
      this.configData = {};
      this.checkItemHistList = [];
      this.allCheckItemHistList = [];
      this.baseHistSeq = null;
      this.compareHistSeq = null;
      this.showCompareText = '';
      this.compareHistData = {};
      this.loadedConfigData = false;
    },
    /**
     * 하드웨어 데이터를 설정한다.
     * @author Ju
     * @return {[type]} [description]
     */
    setHardwareData() {
      if (!isEmpty(this.data) && !isEmpty(this.data.etcInfo)) {
        const etcInfo = this.data.etcInfo;
        this.moduleData = isEmpty(etcInfo[etcInfoDefine.NETDEVICE_MODULE]) ? {} : etcInfo[etcInfoDefine.NETDEVICE_MODULE];
        this.powerData = isEmpty(etcInfo[etcInfoDefine.NETDEVICE_POWER]) ? {} : etcInfo[etcInfoDefine.NETDEVICE_POWER];
        this.fanData = isEmpty(etcInfo[etcInfoDefine.NETDEVICE_FAN]) ? {} : etcInfo[etcInfoDefine.NETDEVICE_FAN];
      }
    },
    /**
     * 공백 데이터 처리 formatter
     * @author Ju
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    formattterForEmptyCheck(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    /**
     * [공통] 날짜 포맷터
     * @author jeee12
     * @param  {[type]} value    [description]
     * @return {[type]}          [description]
     */
    getDateFormatter(value) {
      return isNil(value) ? EMPTY_TEXT : getDateToString(value);
    },
  },
};
</script>

<style>
.config-subtitle-bar {
  padding-top: 10px;
  border-top: 1px solid #e0e0e0;
}
</style>



// WEBPACK FOOTER //
// src/components/popup/FaultAnalysisPopup/components/FaultAnalysisInfoView/EtcInfo/NetdeviceEtcInfoView/ConfigChangeHistView.vue